import _ from 'lodash';

import { TeamMemberFormAccessPermissions } from '../../team/team.constants';
import { TeamMember, TeamMemberSecurityGroup } from '../../team/team.types';

import {
  ProjectOverviewMembersListFormMember,
  ProjectOverviewMembersListFormMemberPermission,
  ProjectOverviewMembersListFormMemberSecurityGroup,
} from './project-overview-members.types';

export const getProjectOverviewMembersListFormSecurityGroupsDifferences = (
  initialMembers: ProjectOverviewMembersListFormMember[],
  submittedMembers: ProjectOverviewMembersListFormMember[],
): ProjectOverviewMembersListFormMemberPermission[] => {
  return _.reduce(
    submittedMembers,
    (
      acc: ProjectOverviewMembersListFormMemberPermission[],
      member: ProjectOverviewMembersListFormMember,
    ): ProjectOverviewMembersListFormMemberPermission[] => {
      const initialMember = _.find(initialMembers, { id: member.id });

      if (!initialMember) {
        return acc;
      }

      if (initialMember.permissions === member.permissions) {
        return acc;
      }

      acc.push({
        id: member.id,
        initialPermission: initialMember.permissions,
        newPermission: member.permissions,
      });

      return acc;
    },
    [],
  );
};

export const generateMembersSecurityGroupsDifferences = (
  initialFormMembers: ProjectOverviewMembersListFormMember[],
  submittedFormMembers: ProjectOverviewMembersListFormMember[],
  projectId: string,
): ProjectOverviewMembersListFormMemberSecurityGroup[] => {
  const securityGroupsDifferences =
    getProjectOverviewMembersListFormSecurityGroupsDifferences(
      initialFormMembers,
      submittedFormMembers,
    );

  if (_.isEmpty(securityGroupsDifferences)) {
    return [];
  }

  return _.map(
    securityGroupsDifferences,
    (
      memberSecurityGroup: ProjectOverviewMembersListFormMemberPermission,
    ): ProjectOverviewMembersListFormMemberSecurityGroup => {
      return {
        id: memberSecurityGroup.id,
        initialSecurityGroup: {
          permission: memberSecurityGroup.initialPermission,
          projectId,
        },
        newSecurityGroup: {
          permission: memberSecurityGroup.newPermission,
          projectId,
        },
      };
    },
  );
};

export const getProjectOverviewMembersSecurityGroupsUpdated = (
  members: TeamMember[],
  updatedMembersSecurityGroups: ProjectOverviewMembersListFormMemberSecurityGroup[],
): TeamMember[] => {
  return _.reduce(
    updatedMembersSecurityGroups,
    (
      acc: TeamMember[],
      memberSecurityGroup: ProjectOverviewMembersListFormMemberSecurityGroup,
    ) => {
      const member = _.find<TeamMember>(members, {
        id: memberSecurityGroup.id,
      });

      if (!member) {
        return acc;
      }

      const securityGroups = _.filter(
        member.securityGroups,
        (securityGroup: TeamMemberSecurityGroup) => {
          return (
            securityGroup.permission !==
              (memberSecurityGroup.initialSecurityGroup.permission as string) ||
            securityGroup.projectId !==
              memberSecurityGroup.initialSecurityGroup.projectId
          );
        },
      );

      const updatedSecurityGroups = [...securityGroups];

      if (
        memberSecurityGroup.newSecurityGroup.permission !==
        TeamMemberFormAccessPermissions.None
      ) {
        updatedSecurityGroups.push(memberSecurityGroup.newSecurityGroup as any);
      }

      acc.push({
        ...member,
        securityGroups: updatedSecurityGroups,
      });

      return acc;
    },
    [],
  );
};

export const generateMemberSecurityGroupsData = (
  memberSecurityGroups: TeamMemberSecurityGroup[],
): string[] => {
  const securityGroups = _.map<TeamMemberSecurityGroup[], string>(
    memberSecurityGroups,
    (group: TeamMemberSecurityGroup) => {
      return group.projectId
        ? `${group.permission}@${group.projectId}`
        : group.permission;
    },
  );

  return _.uniq(securityGroups);
};
